import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ExternalLink, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import DuplicateConfirmDialog from "@/components/DuplicateConfirmDialog";
import useDuplicateConfirm from "@/hooks/useDuplicateConfirm";

// Convert any date format (YYYY-MM-DD, DD/MM/YYYY, French) to YYYY-MM-DD
const parseDateToISO = (dateStr) => {
  if (!dateStr) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const slash = dateStr.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (slash) return `${slash[3]}-${slash[2].padStart(2, "0")}-${slash[1].padStart(2, "0")}`;
  const months = { janvier: "01", fevrier: "02", "février": "02", mars: "03", avril: "04", mai: "05", juin: "06", juillet: "07", aout: "08", "août": "08", septembre: "09", octobre: "10", novembre: "11", decembre: "12", "décembre": "12" };
  const fr = dateStr.toLowerCase().match(/(\d{1,2})\s+(janvier|fevrier|février|mars|avril|mai|juin|juillet|aout|août|septembre|octobre|novembre|decembre|décembre)\s+(\d{4})/);
  if (fr) return `${fr[3]}-${months[fr[2]]}-${fr[1].padStart(2, "0")}`;
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) return d.toISOString().split("T")[0];
  return "";
};

// Extract a Sellsy numeric id from a created/existing entity object
const sellsyIdOf = (e) => {
  if (!e) return null;
  if (e.sellsy_id && /^\d+$/.test(String(e.sellsy_id))) return String(e.sellsy_id);
  if (e.id && /^\d+$/.test(String(e.id))) return String(e.id);
  return null;
};

const QuoteForm = ({
  extractedData,
  apiUrl,
  createdOpportunity,
  createdContact,
  createdCompany,
  existingContact,
  existingCompany,
  createdQuote,
  onQuoteCreated,
}) => {
  const [loading, setLoading] = useState(false);

  // A real Sellsy opportunity is linked only when it has a numeric Sellsy id
  // and was NOT just validated locally.
  const realOppId =
    createdOpportunity && !createdOpportunity.validated_only
      ? createdOpportunity.sellsy_id ||
        (createdOpportunity.id && /^\d+$/.test(String(createdOpportunity.id)) ? createdOpportunity.id : null)
      : null;

  // Resolve a Sellsy company / contact id from any available source:
  // validated opportunity > created in session > detected existing.
  const companyId =
    createdOpportunity?.company_id || sellsyIdOf(createdCompany) || sellsyIdOf(existingCompany);
  const contactId =
    createdOpportunity?.contact_id || sellsyIdOf(createdContact) || sellsyIdOf(existingContact);

  // The quote can be created when linked to a real opportunity OR when a Sellsy
  // company is available (standalone quote — no opportunity needed).
  const canSubmit = !!realOppId || !!companyId;

  const { submit: submitQuote, dialogProps } = useDuplicateConfirm(async (force) => {
    const sep = force ? "?force=true" : "";
    const po = extractedData?.purchase_order_number || null;
    const payload = realOppId
      ? { opportunity_id: realOppId, items: [], notes: "", purchase_order_number: po }
      : {
          opportunity_id: null,
          company_id: companyId,
          contact_id: contactId || null,
          animations: createdOpportunity?.animations || extractedData?.requested_animations || [],
          participants_count:
            createdOpportunity?.participants_count ?? (parseInt(extractedData?.participants_count) || 0),
          event_location: createdOpportunity?.event_location || extractedData?.event_location || null,
          event_horaires: createdOpportunity?.event_horaires || extractedData?.event_horaires || null,
          event_date: createdOpportunity?.event_date || parseDateToISO(extractedData?.event_date) || null,
          event_end_date: createdOpportunity?.event_end_date || parseDateToISO(extractedData?.event_end_date) || null,
          items: [],
          notes: "",
          purchase_order_number: po,
        };
    return axios.post(`${apiUrl}/quotes${sep}`, payload);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Créez d'abord le contact et la société dans Sellsy");
      return;
    }
    setLoading(true);
    try {
      const data = await submitQuote();
      if (!data) {
        toast.info("Création annulée");
        return;
      }
      if (onQuoteCreated) onQuoteCreated(data);
      toast.success("Devis créé dans Sellsy");
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === "string" ? detail : detail?.message || "Erreur lors de la création";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Persisted quote success card
  if (createdQuote) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 space-y-2" data-testid="quote-success-card">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-emerald-700 flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> Devis créé
            </p>
            <p className="text-sm text-emerald-900 font-medium">
              Devis {createdQuote.sellsy_id ? `#${createdQuote.sellsy_id}` : "(local)"}
            </p>
            <p className="text-xs text-emerald-600">
              {(createdQuote.total || 0).toLocaleString("fr-FR")} EUR · {createdQuote.status}
            </p>
          </div>
          {createdQuote.sellsy_url && (
            <a
              href={createdQuote.sellsy_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-600 hover:text-emerald-800 flex-shrink-0 mt-0.5"
              title="Ouvrir le devis dans Sellsy"
              data-testid="sellsy-quote-link"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onQuoteCreated && onQuoteCreated(null)}
          className="w-full h-7 text-xs"
          data-testid="create-another-quote-btn"
        >
          Créer un autre devis
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="quote-form">
      {realOppId ? (
        <div className="bg-slate-50 rounded-md p-2.5 text-xs space-y-1 border border-slate-200" data-testid="quote-preview">
          <p className="font-medium text-slate-700">Lié à l'opportunité :</p>
          <p className="text-slate-900 font-medium">{createdOpportunity.name}</p>
          <p className="text-[10px] text-slate-400">Sellsy #{realOppId}</p>
        </div>
      ) : canSubmit ? (
        <div className="bg-slate-50 rounded-md p-2.5 text-xs space-y-1 border border-slate-200" data-testid="quote-preview">
          <p className="font-medium text-slate-700">Devis direct (sans opportunité) :</p>
          <p className="text-slate-900 font-medium">
            {createdCompany?.name || existingCompany?.name || extractedData?.company || "Société Sellsy"}
          </p>
          <p className="text-[10px] text-slate-400">
            Société Sellsy #{companyId}
            {contactId ? ` · Contact #${contactId}` : ""}
          </p>
          {!contactId && (
            <p className="text-[10px] text-amber-600">Aucun contact lié — créez-le pour un devis complet.</p>
          )}
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-2.5 text-xs text-amber-700 flex items-start gap-2" data-testid="quote-blocked">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>Créez d'abord le contact et la société dans l'onglet Actions. Le devis pourra ensuite être généré directement, sans opportunité.</span>
        </div>
      )}

      <Button
        type="submit"
        disabled={loading || !canSubmit}
        className="w-full h-8 text-sm bg-emerald-600 hover:bg-emerald-700"
        data-testid="quote-submit-btn"
      >
        {loading ? (
          <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Création en cours...</>
        ) : (
          <><ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Créer le devis dans Sellsy</>
        )}
      </Button>

      <p className="text-[10px] text-slate-400 text-center">
        Le devis sera créé dans Sellsy avec les animations et prix du catalogue
      </p>
      <DuplicateConfirmDialog {...dialogProps} />
    </form>
  );
};

export default QuoteForm;
